import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import mediaQuery from "styles/mediaQuery.js";

//assets
import doodle from "assets/gif/doodle-arrow.gif";
import doodleUnderlineGif from "assets/gif/doodle-underline.gif";
import doodleUnderline2PurpleGif from "assets/gif/doodle-underline-2-p.gif";
import doodleCircle from "assets/svg/circle.svg";
import trioCircle from "assets/svg/trio.svg";

// Pure typography only — no position/isolation. Keeps GSAP pin layout stable.
const Bold = styled.span`
  font-weight: 700;
`;

const Italic = styled.span`
  font-style: italic;
`;

// Wraps plain (non-tagged) text so it lives in the same painting layer
// (positioned descendants, stack-level 0) as the decorated <CustomSpan>s.
// Without this, plain text would be in CSS painting Layer 5 and any absolutely
// positioned doodle that spills out of a CustomSpan (Layer 6) would render on
// top of it. Sharing Layer 6 means DOM order decides, so text that comes AFTER
// a doodle span correctly paints on top of the doodle.
const PlainSpan = styled.span`
  position: relative;
`;

const CustomSpan = styled.span`
position: relative;
isolation: isolate;
display: inline-block;
&::before{
  content: "";
  position: absolute;
  z-index: -1;
}
&.underline2{
  img{
    bottom: -30%;
  }
}
&.strong {
  font-weight: 700;
}
&.italic {
  font-style: italic;
}
&.trio {
  &::before{
    content: "";
    bottom: -30%;
    left: 50%;
    transform: translateX(-50%);
    max-height: initial;
    height: 250%;
    width: 150%;
    background-image: url(${trioCircle});
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }
}
&.arrow{
  display: inline-block;
  padding-right: 2em;
  &::before{
    top: 0;
    left: 0rem;
    background-image: url(${doodle});
background-position: left;
background-repeat: no-repeat;
background-size: contain;
    width: 100%;
    height: 100%;
;
  }
}
&.circle{
  &::before{
    background-image: url(${doodleCircle});
    background-position: left;
background-repeat: no-repeat;
background-size: contain;
    width: 100%;
    height: 100%;
  }
}


&.redaction {
  background: #1a1a1a;
  color: #1a1a1a;
  padding: 0.05em 0.15em;
  border-radius: 2px;
  user-select: none;

}

&.redaction-dark {
  background: #fff;
  color: #fff;
}

@media (max-width: ${mediaQuery.tablet}) {
  /* &.redaction,
  &.redaction-dark {
    background: transparent;
    color: inherit;
    padding: 0;
  } */
}

`;

const DecorationImg = styled.img`
  position: absolute;
  pointer-events: none;
  user-select: none;
  left: 50%;
  bottom: -10%;
  transform: translateX(-50%);
  width: 100%;
  height: 120%;
  max-height: 0.7em;
  object-fit: contain;
  z-index: -1;
`;

const DecorationImgStrike = styled(DecorationImg)`
  bottom: initial;
  top: 50%;
  transform: translateY(-50%) translateX(-50%);
  z-index: 10;
`;

function getGifForClassName(className) {
  if (!className) return null;
  const cn = String(className);

  if (cn.includes("underline2") && cn.includes("purple"))
    return doodleUnderline2PurpleGif;
  if (cn.includes("strikethrough2")) return doodleUnderline2PurpleGif;
  if (cn.includes("underline") || cn.includes("strikethrough"))
    return doodleUnderlineGif;

  return null;
}

const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==";

// Scroll-into-view GIF play without React remounts.
// Mounting/unmounting <img> during GSAP pin causes scroll jumps;
// swapping src via ref keeps DOM structure stable.
function LazyGifSpan({ className, children, ...rest }) {
  const elRef = useRef(null);
  const imgRef = useRef(null);
  const wasInViewRef = useRef(false);

  const gifSrc = getGifForClassName(className);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!elRef.current) return undefined;
    if (!gifSrc) return undefined;

    const play = () => {
      const img = imgRef.current;
      if (!img) return;
      img.src = TRANSPARENT_PIXEL;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (imgRef.current) imgRef.current.src = gifSrc;
        });
      });
    };

    if (!("IntersectionObserver" in window)) {
      play();
      return undefined;
    }

    let timerId;
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timerId);
        if (entry.isIntersecting) {
          if (wasInViewRef.current) return;
          wasInViewRef.current = true;
          timerId = window.setTimeout(play, 500);
        } else {
          wasInViewRef.current = false;
        }
      },
      { root: null, threshold: 0 },
    );

    observer.observe(elRef.current);
    return () => {
      clearTimeout(timerId);
      observer.disconnect();
    };
  }, [gifSrc]);

  const isStrike = className?.includes("strikethrough");
  const Tag = isStrike ? DecorationImgStrike : DecorationImg;

  return (
    <CustomSpan ref={elRef} className={className} {...rest}>
      {gifSrc ? (
        <Tag
          ref={imgRef}
          src={TRANSPARENT_PIXEL}
          alt=""
          aria-hidden="true"
          decoding="async"
        />
      ) : null}
      {children}
    </CustomSpan>
  );
}

function TextWithLineBreaks(props) {
  if (typeof props.text !== "string") {
    return null;
  }

  const parseTextWithTags = (text, lineIndex) => {
    const parts = [];
    // **bold** before *italic* so **...** is not eaten by single-*
    const regex =
      /\{b\}(.*?)\{\/b\}|\*\*(.+?)\*\*|\*([^*\n]+?)\*|>([^<]+)<([^>]+)>/g;
    let lastIndex = 0;
    let match;
    let partIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <PlainSpan key={`line-${lineIndex}-part-${partIndex++}`}>
            {text.substring(lastIndex, match.index)}
          </PlainSpan>,
        );
      }
      if (match[1] !== undefined) {
        parts.push(
          <Bold key={`line-${lineIndex}-bold-${partIndex++}`}>{match[1]}</Bold>,
        );
      } else if (match[2] !== undefined) {
        parts.push(
          <Bold key={`line-${lineIndex}-bold-md-${partIndex++}`}>
            {match[2]}
          </Bold>,
        );
      } else if (match[3] !== undefined) {
        parts.push(
          <Italic key={`line-${lineIndex}-italic-${partIndex++}`}>
            {match[3]}
          </Italic>,
        );
      } else {
        const hasRedaction = match[5].includes("redaction");
        const className = `${match[5]} ${props.purple ? "purple" : ""}${
          props.dark && hasRedaction ? " redaction-dark" : ""
        }`;
        const hasGif = Boolean(getGifForClassName(className));
        parts.push(
          hasGif ? (
            <LazyGifSpan
              className={className}
              key={`line-${lineIndex}-class-${partIndex++}`}
            >
              {match[4]}
            </LazyGifSpan>
          ) : (
            <CustomSpan
              className={className}
              key={`line-${lineIndex}-class-${partIndex++}`}
            >
              {match[4]}
            </CustomSpan>
          ),
        );
      }
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(
        <PlainSpan key={`line-${lineIndex}-part-${partIndex++}`}>
          {text.substring(lastIndex)}
        </PlainSpan>,
      );
    }

    return parts.length > 0 ? parts : text;
  };

  const textWithBreaks = props.text.split("\n").map((text, index, array) => (
    <React.Fragment key={index}>
      {parseTextWithTags(text, index)}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));

  return <>{textWithBreaks}</>;
}

export default TextWithLineBreaks;
